import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ModalController } from '@ionic/angular/standalone';
import { IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent, IonList, IonItem, IonLabel, IonInput, IonSelect, IonSelectOption, IonCheckbox, IonFooter, IonNote } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { close } from 'ionicons/icons';
import { Subscription } from 'rxjs';
import { UserProfile, ExpertiseArea, SDG } from '../../models/profile.model';
import { Country } from '../../models/country.model';
import { Language } from '../../models/language.model';
import { getAffiliationName, knownAffiliations, availableFieldsOfScience, availableLanguages, availableExpertiseAreas, availableSdgs, ALL, availableCountries, availableFocusAreas } from '../../constants/constants';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ModalService } from '../../services/modal.service';
import { ProfileService } from '../../services/profile.service';
import { TranslateService } from '../../services/translate.service';
import { AffiliationService } from '../../services/affiliation.service';

@Component({
  selector: 'app-edit-profile-modal',
  templateUrl: './edit-profile-modal.component.html',
  styleUrls: ['./edit-profile-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonInput,
    IonSelect,
    IonSelectOption,
    IonCheckbox,
    IonFooter,
    IonNote
  ]
})
export class EditProfileModalComponent implements OnInit, OnDestroy {
  @Input() profile!: UserProfile;
  editedProfile!: UserProfile;
  expertStatus: boolean = false;
  previousExpertStatus: boolean = false;
  allFieldsFilledForExpert: boolean = false;
  showExpertTooltip: boolean = false;
  expertFieldErrors: { [key: string]: boolean } = {};
  websiteErrors: { [key: string]: boolean } = {};
  availableAffiliations: any[] = [];
  availableFieldsOfScience = availableFieldsOfScience.filter(field => field.name !== ALL);
  availableLanguages: Language[] = availableLanguages.filter(lang => lang.name !== ALL);
  availableExpertiseAreas: ExpertiseArea[] = availableExpertiseAreas.filter(area => area.name !== ALL);
  availableSdgs: SDG[] = availableSdgs.filter(sdg => sdg.name !== ALL);
  selectedCountry: Country | null = null;
  selectedLanguages: Language[] = [];
  selectedExpertiseAreas: ExpertiseArea[] = [];
  private subscriptions = new Subscription();
  readonly availableFocusAreas = availableFocusAreas;
  @ViewChild('editForm') editForm!: NgForm;

  constructor(
    private modalCtrl: ModalController,
    private modalService: ModalService,
    private profileService: ProfileService,
    private affiliationService: AffiliationService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    addIcons({ close });
  }

  ngOnInit() {
    this.editedProfile = JSON.parse(JSON.stringify(this.profile)); // Deep copy
    this.editedProfile.mediaContactConsent ??= false;
    const affSub = this.affiliationService.affiliations$.subscribe(affiliations => {
      this.availableAffiliations = affiliations.filter(aff => aff.name !== ALL);
    });
    this.subscriptions.add(affSub);
    this.expertStatus = this.editedProfile.status === 'active' || this.editedProfile.status === 'pending';
    this.previousExpertStatus = this.expertStatus;
    this.updateSelectedValues();
    this.validateForm();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  updateSelectedValues() {
    if (this.editedProfile) {
      this.selectedCountry = availableCountries.find(c => c.name === this.editedProfile.country) || null;
      this.selectedLanguages = this.availableLanguages.filter(lang => this.editedProfile!.languages?.includes(lang.name)) || [];
      this.selectedExpertiseAreas = this.availableExpertiseAreas.filter(area => this.editedProfile!.expertiseAreas?.includes(area.name)) || [];
      this.onInputChange();
    }
  }

  onInputChange() {
    this.validateWebsites();
    if (this.expertStatus) {
      this.checkAllFieldsForExpert();
    }
    this.validateForm();
  }

  onExpertStatusChange() {
    if (this.expertStatus) {
      this.checkAllFieldsForExpert();
    } else {
      this.allFieldsFilledForExpert = true;
      this.resetExpertFieldErrors();
    }
    this.onInputChange();
  }

  validateForm(): boolean {
    if (!this.editedProfile) return false;
    let isValid = true;
    if (!this.editedProfile.name.firstname || !this.editedProfile.name.lastname || !this.editedProfile.affiliation) {
      isValid = false;
    }
    if (this.expertStatus && !this.allFieldsFilledForExpert) {
      isValid = false;
    }
    return isValid;
  }

  private resetExpertFieldErrors() {
    Object.keys(this.expertFieldErrors).forEach(key => {
      this.expertFieldErrors[key] = false;
    });
  }

  private checkAllFieldsForExpert() {
    this.expertFieldErrors['country'] = !this.editedProfile.country;
    this.expertFieldErrors['languages'] = this.editedProfile.languages.length === 0;
    this.expertFieldErrors['fieldOfStudy'] = !this.editedProfile.fieldOfStudy;
    this.expertFieldErrors['expertiseAreas'] = this.editedProfile.expertiseAreas.length === 0;
    this.expertFieldErrors['focusAreas'] = !this.editedProfile.focusAreas || this.editedProfile.focusAreas.length === 0;
    this.expertFieldErrors['professionalReference'] = ![
      this.editedProfile.institutionalWebpage,
      this.editedProfile.personalWebsite,
      this.editedProfile.researchGroupWebsite,
    ].some((website) => website?.trim());
    const hasWebsiteErrors = Object.values(this.websiteErrors).some(error => error);
    this.allFieldsFilledForExpert = !Object.values(this.expertFieldErrors).some(err => err) && !hasWebsiteErrors;
  }

  private validateWebsites() {
    const urlPattern = /^(https?:\/\/)?[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;
    this.websiteErrors['institutionalWebpage'] = !!this.editedProfile.institutionalWebpage && !urlPattern.test(this.editedProfile.institutionalWebpage);
    this.websiteErrors['personalWebsite'] = !!this.editedProfile.personalWebsite && !urlPattern.test(this.editedProfile.personalWebsite);
    this.websiteErrors['researchGroupWebsite'] = !!this.editedProfile.researchGroupWebsite && !urlPattern.test(this.editedProfile.researchGroupWebsite);
  }

  isInAffiliations(value: string): boolean {
    return this.availableAffiliations.some(aff => aff.value === value);
  }

  isOtherAffiliation(): boolean {
    if (!this.editedProfile) return false;
    return this.editedProfile.affiliation === 'other' ||
      !knownAffiliations.some(affiliation => affiliation.value === this.editedProfile.affiliation);
  }

  getAffiliationName = getAffiliationName;

  isInFieldsOfScience(value: string): boolean {
    return this.availableFieldsOfScience.some(field => field.value === value);
  }

  isOtherFieldOfStudy(): boolean {
    if (!this.editedProfile) return false;
    return this.editedProfile.fieldOfStudy === 'other' || !this.isInFieldsOfScience(this.editedProfile.fieldOfStudy);
  }

  async openCountryModal() {
    const selected = await this.modalService.openCountrySelectionModal(this.selectedCountry);
    if (selected) {
      this.selectedCountry = selected;
      this.editedProfile.country = selected.name;
      this.onInputChange();
      this.cdr.detectChanges();
    }
  }

  async openLanguageModal() {
    const selected = await this.modalService.openLanguageSelectionModal(this.selectedLanguages);
    if (selected) {
      this.selectedLanguages = selected;
      this.editedProfile.languages = selected.map(l => l.name);
      this.onInputChange();
      this.cdr.detectChanges();
    }
  }

  async openExpertiseAreasModal() {
    const selected = await this.modalService.openExpertiseAreasModal(this.selectedExpertiseAreas);
    if (selected) {
      this.selectedExpertiseAreas = selected;
      this.editedProfile.expertiseAreas = selected.map(a => a.name);
      this.onInputChange();
      this.cdr.detectChanges();
    }
  }

  getSelectedItemsString(items: { name: string }[]): string {
    return this.profileService.getSelectedItemsString(items);
  }

  private determineExpertStatus(): string {
    if (!this.expertStatus) {
      return 'none';
    }

    if (this.profile?.status === 'active' && this.expertStatus) {
      return 'active';
    }

    if (this.profile?.status === 'pending' && this.expertStatus) {
      return 'pending';
    }

    return 'pending';
  }

  async onCancel() {
    if (this.editForm?.dirty) {
        const { data } = await this.modalService.openConfirmationModal({
            title: 'UNSAVED_CHANGES_TITLE',
            message: 'UNSAVED_CHANGES_MESSAGE',
            confirmText: 'DISCARD',
            cancelText: 'CONTINUE_EDITING'
        });
        if (data && data.confirmed) {
            this.modalCtrl.dismiss({ saved: false });
        }
    } else {
        this.modalCtrl.dismiss({ saved: false });
    }
  }

  onSubmit() {
    if (!this.validateForm()) {
      return;
    }
    const updatedProfile = {
      ...this.editedProfile,
      institutionalWebpage: this.editedProfile.institutionalWebpage?.trim() || '',
      personalWebsite: this.editedProfile.personalWebsite?.trim() || '',
      researchGroupWebsite: this.editedProfile.researchGroupWebsite?.trim() || '',
      status: this.determineExpertStatus()
    };
    this.modalCtrl.dismiss({ saved: true, profile: updatedProfile });
  }
}
